import React, { useState } from 'react';

const ImageUpload = ({ onImageUploaded, existingUrl = '' }) => {
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(existingUrl);

  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', uploadPreset);
      formData.append('folder', 'art-marketplace');

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        {
          method: 'POST',
          body: formData
        }
      );

      const data = await response.json();
      
      if (data.secure_url) {
        setImageUrl(data.secure_url);
        onImageUploaded(data.secure_url);
        alert('Image uploaded successfully!');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Preview */}
      {imageUrl && (
        <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-gray-100 border-2 border-orange-200">
          <img
            src={imageUrl}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = 'https://placehold.co/400x300/fb923c/ffffff?text=Image+Preview';
            }}
          />
        </div>
      )}

      {/* Upload Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Upload Button */}
        <label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            disabled={uploading}
            className="hidden"
          />
          <div className={`h-full py-6 px-6 rounded-xl border-2 border-dashed text-center cursor-pointer transition-all duration-200 ${
            uploading
              ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
              : 'border-orange-300 hover:border-orange-500 hover:bg-orange-50'
          }`}>
            {uploading ? (
              <div className="flex flex-col items-center justify-center space-y-2">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-600"></div>
                <span className="text-gray-600 font-semibold">Uploading...</span>
              </div>
            ) : (
              <div>
                <svg className="w-10 h-10 mx-auto mb-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm font-bold text-gray-900 mb-1">
                  📱 Upload from Device
                </p>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF up to 5MB
                </p>
              </div>
            )}
          </div>
        </label>

        {/* Manual URL Input */}
        <div className="flex flex-col justify-center">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            🔗 Or paste image URL
          </label>
          <input
            type="url"
            value={imageUrl}
            onChange={(e) => {
              setImageUrl(e.target.value);
              onImageUploaded(e.target.value);
            }}
            placeholder="https://example.com/image.jpg"
            className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 outline-none transition-all duration-200"
          />
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;